import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  X,
  Lightning,
  ListChecks,
  CalendarBlank,
  FlagBanner,
  Briefcase,
  CaretDown,
  CaretUp,
  Trash,
  Star,
  MagnifyingGlass,
  Note
} from '@phosphor-icons/react';

const API = `${window.location.origin}/api`;

const DUE_DATE_LABELS = {
  today: 'Due today',
  tomorrow: 'Due tomorrow',
  in_3_days: 'Due in 3 days',
  in_7_days: 'Due in 7 days',
  in_14_days: 'Due in 2 weeks',
  next_monday: 'Due next Monday',
  next_friday: 'Due next Friday',
  end_of_week: 'Due end of week',
};

function WorkflowTemplatePickerModal({ onClose, onTaskCreated }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [applying, setApplying] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API}/workflow-templates`, { withCredentials: true });
      setTemplates(res.data);
    } catch (err) {
      console.error('Failed to fetch workflow templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const applyTemplate = async (templateId) => {
    setApplying(templateId);
    try {
      const res = await axios.post(
        `${API}/workflow-templates/${templateId}/apply`,
        {},
        { withCredentials: true }
      );
      if (onTaskCreated) onTaskCreated(res.data);
      onClose();
    } catch (err) {
      console.error('Failed to apply workflow template:', err);
      alert('Failed to create task from template.');
    } finally {
      setApplying(null);
    }
  };

  const deleteTemplate = async (templateId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this workflow template?')) return;
    try {
      await axios.delete(`${API}/workflow-templates/${templateId}`, { withCredentials: true });
      setTemplates(templates.filter(t => t.wf_template_id !== templateId));
    } catch (err) {
      console.error('Failed to delete template:', err);
    }
  };

  const filtered = search.trim()
    ? templates.filter(t => t.name.toLowerCase().includes(search.toLowerCase()) || t.default_title.toLowerCase().includes(search.toLowerCase()))
    : templates;

  const systemTemplates = filtered.filter(t => t.is_system);
  const userTemplates = filtered.filter(t => !t.is_system);

  const renderTemplateCard = (tmpl) => {
    const isExpanded = expanded === tmpl.wf_template_id;
    const checklistCount = (tmpl.checklist_items || []).length;

    return (
      <div
        key={tmpl.wf_template_id}
        className="border border-slate-200 rounded-lg overflow-hidden hover:border-blue-200 transition-colors"
        data-testid={`wf-template-${tmpl.wf_template_id}`}
      >
        <div
          className="flex items-center gap-3 p-3.5 cursor-pointer"
          onClick={() => setExpanded(isExpanded ? null : tmpl.wf_template_id)}
        >
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${tmpl.is_system ? 'bg-amber-50 text-amber-600' : 'bg-blue-50 text-navy'}`}>
            {tmpl.is_system ? <Star size={16} weight="fill" /> : <Lightning size={16} weight="fill" />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 truncate">{tmpl.name}</p>
            <div className="flex items-center gap-2 text-xs text-slate-400 mt-0.5">
              {tmpl.due_date_rule && <span>{DUE_DATE_LABELS[tmpl.due_date_rule] || tmpl.due_date_rule}</span>}
              {checklistCount > 0 && (
                <span className="flex items-center gap-0.5"><ListChecks size={11} /> {checklistCount} steps</span>
              )}
              {tmpl.is_system && <span className="text-amber-500 font-medium">System</span>}
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {!tmpl.is_system && (
              <button
                onClick={(e) => deleteTemplate(tmpl.wf_template_id, e)}
                className="text-slate-300 hover:text-red-500 transition-colors p-1"
                data-testid={`delete-wf-${tmpl.wf_template_id}`}
              >
                <Trash size={13} />
              </button>
            )}
            {isExpanded ? <CaretUp size={14} className="text-slate-400" /> : <CaretDown size={14} className="text-slate-400" />}
          </div>
        </div>

        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="px-3.5 pb-3.5 border-t border-slate-100 pt-3 space-y-2.5">
                {/* Preview fields */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-slate-50 rounded-lg p-2">
                    <p className="text-[10px] uppercase tracking-wide text-slate-400 font-medium mb-0.5">Task Title</p>
                    <p className="text-xs text-slate-700 font-medium">{tmpl.default_title}</p>
                  </div>
                  {tmpl.default_project_client && (
                    <div className="bg-slate-50 rounded-lg p-2">
                      <p className="text-[10px] uppercase tracking-wide text-slate-400 font-medium mb-0.5 flex items-center gap-1"><Briefcase size={10} /> Project</p>
                      <p className="text-xs text-slate-700">{tmpl.default_project_client}</p>
                    </div>
                  )}
                  {tmpl.default_priority && (
                    <div className="bg-slate-50 rounded-lg p-2">
                      <p className="text-[10px] uppercase tracking-wide text-slate-400 font-medium mb-0.5 flex items-center gap-1"><FlagBanner size={10} /> Priority</p>
                      <p className="text-xs text-slate-700 capitalize">{tmpl.default_priority}</p>
                    </div>
                  )}
                  {tmpl.due_date_rule && (
                    <div className="bg-slate-50 rounded-lg p-2">
                      <p className="text-[10px] uppercase tracking-wide text-slate-400 font-medium mb-0.5 flex items-center gap-1"><CalendarBlank size={10} /> Due Date</p>
                      <p className="text-xs text-slate-700">{DUE_DATE_LABELS[tmpl.due_date_rule] || tmpl.due_date_rule}</p>
                    </div>
                  )}
                </div>

                {tmpl.notes && (
                  <div className="bg-slate-50 rounded-lg p-2">
                    <p className="text-[10px] uppercase tracking-wide text-slate-400 font-medium mb-0.5 flex items-center gap-1"><Note size={10} /> Instructions</p>
                    <p className="text-xs text-slate-600">{tmpl.notes}</p>
                  </div>
                )}

                {checklistCount > 0 && (
                  <div className="bg-slate-50 rounded-lg p-2">
                    <p className="text-[10px] uppercase tracking-wide text-slate-400 font-medium mb-1 flex items-center gap-1"><ListChecks size={10} /> Checklist ({checklistCount} items)</p>
                    <ul className="space-y-0.5">
                      {tmpl.checklist_items.map((item, i) => (
                        <li key={i} className="text-xs text-slate-600 flex items-center gap-1.5">
                          <span className="w-1 h-1 rounded-full bg-slate-400 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <button
                  onClick={() => applyTemplate(tmpl.wf_template_id)}
                  disabled={applying === tmpl.wf_template_id}
                  className="w-full bg-navy text-white hover:bg-navy-hover transition-colors font-medium px-3 py-2.5 rounded-lg text-sm disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid={`apply-wf-${tmpl.wf_template_id}`}
                >
                  <Lightning size={14} weight="fill" />
                  {applying === tmpl.wf_template_id ? 'Creating task...' : 'Create Task from Template'}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-[60]" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-lg shadow-lg max-w-lg w-full max-h-[85vh] flex flex-col"
        data-testid="workflow-template-picker"
      >
        {/* Header */}
        <div className="border-b border-slate-200 p-5 flex items-center justify-between flex-shrink-0">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Lightning size={20} weight="duotone" className="text-navy" />
            Workflow Templates
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="close-wf-picker">
            <X size={22} />
          </button>
        </div>

        {/* Search */}
        <div className="px-5 pt-4 flex-shrink-0">
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search templates..."
              className="bg-slate-50 border border-slate-200 rounded-lg pl-9 pr-4 py-2.5 w-full text-sm text-slate-900 focus:border-navy focus:ring-2 focus:ring-blue-100"
              data-testid="wf-template-search"
            />
          </div>
        </div>

        {/* Template list */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-navy border-t-transparent" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-8" data-testid="no-wf-templates">
              <p className="text-sm text-slate-500">{templates.length === 0 ? 'No workflow templates available.' : 'No matching templates.'}</p>
            </div>
          ) : (
            <>
              {systemTemplates.length > 0 && (
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Starter Templates</p>
                  <div className="space-y-2">
                    {systemTemplates.map(renderTemplateCard)}
                  </div>
                </div>
              )}
              {userTemplates.length > 0 && (
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Your Templates</p>
                  <div className="space-y-2">
                    {userTemplates.map(renderTemplateCard)}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}

export default WorkflowTemplatePickerModal;
